import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { ENABLE_COUPONS } from '#client/constants/experimentsFlags';
import { useProductPrice } from '#client/pages/ProductLanding/hooks/useProductPrice';
import { useServiceFlags } from '#client/pages/ProductLanding/hooks/useServiceFlags';

import * as Styled from './BuyBox.style';
import { useBuyBoxContext } from './BuyBoxContext';
import { meetsCouponFlagRequirement } from './coupons/utils';

export const getDisplayPrice = (amount, interval, showZero = false) => {
  if (amount === 0 && !showZero) return '';

  if (typeof amount !== 'number' || Number.isNaN(amount)) return '';

  const formatted = `$${amount}`;
  switch (interval) {
    case 'monthly':
      return `${formatted}/month`;
    case 'annual':
      return `${formatted}/yr`;
    default:
      return formatted;
  }
};

function TitleSection() {
  const {
    ctaProps,
    enabledCoupon,
    interval: intervalFromContext,
    productName,
    serviceCode
  } = useBuyBoxContext();

  const isNewCouponsEnabled = useExperimentFlag(ENABLE_COUPONS, false);
  const flagValues = useServiceFlags(serviceCode);

  const couponToApply = meetsCouponFlagRequirement(
    serviceCode,
    isNewCouponsEnabled,
    enabledCoupon,
    flagValues
  ) ? enabledCoupon : null;

  const {
    amount,
    fees,
    interval,
    originalAmount
  } = useProductPrice(serviceCode, intervalFromContext, couponToApply);

  const ownedCtaText = !ctaProps.isBuyEnabled ? ctaProps.ownedCtaText : null;

  const renderPriceDisplay = () => {
    if (ownedCtaText) {
      return <FormattedMessage id={ownedCtaText} />;
    }

    if (isNewCouponsEnabled && couponToApply?.amount) {
      return (
        <Styled.DiscountPriceContainer>
          <Stack direction="row" alignItems="center" gap={1}>
            <Styled.StrikethroughPrice>
              {getDisplayPrice(originalAmount, interval)}
            </Styled.StrikethroughPrice>
            <Styled.BuyBoxText color="green">
              {getDisplayPrice(amount, interval, true)}
            </Styled.BuyBoxText>
          </Stack>

          {fees && (
            <Styled.FeeText>
              <FormattedMessage id={fees} />
            </Styled.FeeText>
          )}

          <Text color="green" size="sm" weight="medium" sx={{ textAlign: 'right' }}>
            <FormattedMessage
              id="productPage.common.buyBox.couponMessage"
              values={{
                couponAmount: Math.max((originalAmount || 0) - (amount || 0), 0)
              }}
            />
          </Text>
        </Styled.DiscountPriceContainer>
      );
    }

    return (
      <>
        <Styled.BuyBoxText>
          {getDisplayPrice(amount, interval)}
        </Styled.BuyBoxText>
        {fees && (
          <Styled.FeeText>
            <FormattedMessage id={fees} />
          </Styled.FeeText>
        )}
      </>
    );
  };

  const displayPrice = renderPriceDisplay();

  return (
    <Stack
      flexDirection="row"
      gap={2}
      alignItems="center"
      justifyContent="space-between"
    >
      <Styled.BuyBoxText weight="medium" color="primary">
        <FormattedMessage id={productName} />
      </Styled.BuyBoxText>
      {displayPrice && (
        <Styled.PriceContainer>
          {displayPrice}
        </Styled.PriceContainer>
      )}
    </Stack>
  );
}

export default TitleSection;
